import { adminFetch, apiFetch, ApiError, publicManagementFetch } from './client';
import { adminBillingApi } from './admin-billing';

export interface AdminGoogleSignInResponse {
  token: string;
  admin: { email: string; role: string };
}

export interface TenantSummary {
  tenantId: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  userCount: number;
  createdAt: string;
}

export interface SystemHealthInfo {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  uptime: number;
  lastCheck: string;
  details?: Record<string, unknown>;
}

export interface DashboardMetrics {
  totalTenants: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  recentSignups: TenantSummary[];
}

export interface PlatformAdminAllowlist {
  admins: string[];
  currentAdminEmail: string;
  mutable: boolean;
  parameterName?: string;
  version?: number;
  updatedAt?: string;
}

/**
 * Mirrors management's AlarmState (management/src/routes/admin-alarms.ts) —
 * kept in sync manually since alarm data isn't in @grid2/shared-types.
 */
export type AdminAlarmState = 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';

/** Mirrors management's AlarmInfo (GET /admin/alarms response shape). */
export interface AdminAlarmInfo {
  name: string;
  state: AdminAlarmState;
  reason?: string;
  stateUpdatedAt?: string;
  metric?: string;
  description?: string;
}

/**
 * Mirrors management's AlarmsResponse. `degraded: true` means CloudWatch
 * couldn't be reached or the task role lacks cloudwatch:DescribeAlarms —
 * the route still responds 200 with an empty `alarms` array in that case
 * rather than failing, so this is a "data may be incomplete" signal, not
 * a fetch error.
 */
export interface AdminAlarmsResponse {
  alarms: AdminAlarmInfo[];
  degraded: boolean;
  error?: string;
}

interface RawTenantSummary {
  tenantId: string;
  name: string;
  slug: string;
  status: string;
  plan?: string;
  userCount?: number;
  createdAt: string;
}

interface BasicHealthResponse {
  status?: string;
  timestamp?: string;
  uptime?: number;
  checks?: Record<string, unknown>;
}

function normalizeTenant(tenant: RawTenantSummary): TenantSummary {
  return {
    tenantId: tenant.tenantId,
    name: tenant.name,
    slug: tenant.slug,
    status: tenant.status,
    plan: tenant.plan ?? 'unassigned',
    userCount: tenant.userCount ?? 0,
    createdAt: tenant.createdAt,
  };
}

function normalizeHealthStatus(status: unknown): SystemHealthInfo['status'] {
  if (status === 'ok' || status === 'healthy' || status === true) return 'healthy';
  if (status === 'degraded' || status === 'unavailable') return 'degraded';
  return 'down';
}

/**
 * A non-2xx health/readiness response's body (e.g.
 * `{ status: 'unavailable', ready: false, reason: 'grpc_tls_config_fatal' }`)
 * is not itself an `{ error }`/`{ message }` envelope, so `apiFetch`'s
 * `ApiError.message` ends up being the raw JSON body text (see
 * `parseApiErrorBody` in `./client`). Pull the `reason` field back out of
 * that raw text so the UI can show a short, human string instead of the
 * whole blob. Returns undefined for any error message that isn't JSON with
 * a string `reason`.
 */
function extractReadinessReason(message: string): string | undefined {
  try {
    const parsed = JSON.parse(message) as { reason?: unknown };
    return typeof parsed.reason === 'string' ? parsed.reason : undefined;
  } catch {
    return undefined;
  }
}

async function healthCheck(service: string, path: string): Promise<SystemHealthInfo> {
  const checkedAt = new Date().toISOString();

  try {
    const data = await apiFetch<BasicHealthResponse>(path);
    return {
      service,
      status: normalizeHealthStatus(data.status ?? true),
      uptime: typeof data.uptime === 'number' ? data.uptime : 0,
      lastCheck: data.timestamp ?? checkedAt,
      details: data.checks ?? {},
    };
  } catch (err) {
    if (err instanceof ApiError) {
      const reason = extractReadinessReason(err.message);
      if (reason) {
        return {
          service,
          status: 'down',
          uptime: 0,
          lastCheck: checkedAt,
          details: { reason, status: err.status },
        };
      }
    }
    return {
      service,
      status: 'down',
      uptime: 0,
      lastCheck: checkedAt,
      details: {
        error: err instanceof Error ? err.message : 'Health check failed',
      },
    };
  }
}

export const adminApi = {
  completeGoogleSignIn: () =>
    publicManagementFetch<AdminGoogleSignInResponse>('/mgmt/admin/oauth/token', {
      method: 'POST',
    }),

  logout: () =>
    apiFetch<void>('/mgmt/admin/logout', { method: 'POST' }),

  getMe: () =>
    adminFetch<{ admin: { email: string; role: string } }>('/mgmt/admin/me'),

  getDashboard: async (): Promise<DashboardMetrics> => {
    const [tenants, billingStats] = await Promise.all([
      adminApi.getTenants(),
      adminBillingApi.getStats(),
    ]);
    const recentSignups = [...tenants]
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      .slice(0, 5);

    return {
      totalTenants: tenants.length,
      activeSubscriptions: billingStats.totalSubscribers,
      monthlyRevenue: billingStats.mrr,
      recentSignups,
    };
  },

  getTenants: (search?: string) =>
    adminFetch<{ tenants: RawTenantSummary[] }>(
      `/mgmt/admin/tenants${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    ).then((res) => res.tenants.map(normalizeTenant)),

  getTenant: (id: string) =>
    adminFetch<RawTenantSummary>(`/mgmt/admin/tenants/${encodeURIComponent(id)}`).then(
      normalizeTenant,
    ),

  getHealth: () =>
    Promise.all([
      healthCheck('Web/API', '/health'),
      // Readiness carries failure detail liveness deliberately hides — e.g.
      // a 503 body { reason: 'grpc_tls_config_fatal' } when the BFF's
      // control-plane TLS is permanently misconfigured. healthCheck's catch
      // parses the ApiError message as JSON and, when it contains a string
      // `reason` field, surfaces details: { reason, status } instead of the
      // raw response body (see extractReadinessReason above); any other
      // failure (network error, non-JSON body, JSON without a `reason`)
      // falls back to the generic details: { error } path.
      healthCheck('Web/API readiness', '/health/ready'),
      healthCheck('Management', '/mgmt/admin/health'),
    ]),

  getAlarms: () => adminFetch<AdminAlarmsResponse>('/mgmt/admin/alarms'),

  getPlatformAdmins: () =>
    adminFetch<PlatformAdminAllowlist>('/mgmt/admin/platform-admins'),

  addPlatformAdmin: (email: string, expectedVersion: number) =>
    adminFetch<PlatformAdminAllowlist>('/mgmt/admin/platform-admins', {
      method: 'POST',
      body: JSON.stringify({ email, expectedVersion }),
    }),

  removePlatformAdmin: (email: string, expectedVersion: number) =>
    adminFetch<PlatformAdminAllowlist>(
      `/mgmt/admin/platform-admins/${encodeURIComponent(email)}?expectedVersion=${expectedVersion}`,
      { method: 'DELETE' },
    ),
};
